/**
 * Login/Register Page Component
 */
import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { toast } from "sonner";
import { Shield, Gift } from "lucide-react";
import { useAuth } from "@/App";
import api from "@/lib/api";

const LoginPage = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Get referral code from URL
  const searchParams = new URLSearchParams(window.location.search);
  const referralCode = searchParams.get("ref");
  
  // Auto-switch to register if referral code present
  useEffect(() => {
    if (referralCode) {
      setIsRegister(true);
    }
  }, [referralCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Get reCAPTCHA token (v3 - invisible)
      let recaptchaToken = null;
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha(isRegister ? 'register' : 'login');
      }
      
      if (isRegister) {
        const response = await api.post("/auth/register", { email, password, name, recaptcha_token: recaptchaToken });
        localStorage.setItem("wysspr_token", response.data.token);
        setUser(response.data.user);
        toast.success("Account created successfully!");
        
        // Apply referral code if present
        if (referralCode) {
          try {
            await api.post(`/referral/apply?referral_code=${referralCode}`);
            toast.success("Referral bonus applied! You earned 50 points!");
          } catch (refError) {
            console.error("Failed to apply referral:", refError);
          }
        }
      } else {
        const response = await api.post("/auth/login", { email, password, recaptcha_token: recaptchaToken });
        localStorage.setItem("wysspr_token", response.data.token);
        setUser(response.data.user);
        toast.success("Welcome back, Hunter!");
      }
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      // Get Google OAuth URL from backend
      const response = await api.get("/auth/google/url");
      // Redirect to Google OAuth
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Failed to get Google auth URL:", error);
      toast.error("Failed to initialize Google login");
    }
  };

  return (
    <div className="min-h-screen pt-20 px-4 flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Referral Banner */}
        {referralCode && (
          <div className="mb-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Gift className="w-5 h-5 text-purple-400" />
              <span className="text-purple-300 font-semibold">You've Been Referred!</span>
            </div>
            <p className="text-slate-300 text-sm">
              Create an account to claim your <span className="text-cyan-400 font-bold">50 bonus points</span>
            </p>
          </div>
        )}

        <div className="text-center mb-8">
          <Shield className="w-16 h-16 text-cyan-500 mx-auto mb-4" />
          <h1 className="font-heading font-bold text-3xl text-white uppercase">
            {isRegister ? "Create Account" : "Access Platform"}
          </h1>
          <p className="text-slate-400 mt-2">
            {isRegister ? "Join the fight against crime" : "Welcome back, hunter"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card-tactical p-6 space-y-4">
          {isRegister && (
            <div>
              <label className="block text-slate-400 text-sm uppercase tracking-wider mb-2">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full input-tactical p-3"
                data-testid="name-input"
              />
            </div>
          )}
          
          <div>
            <label className="block text-slate-400 text-sm uppercase tracking-wider mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full input-tactical p-3"
              data-testid="email-input"
            />
          </div>
          
          <div>
            <label className="block text-slate-400 text-sm uppercase tracking-wider mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full input-tactical p-3"
              data-testid="password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <span className="animate-spin w-5 h-5 border-2 border-black border-t-transparent rounded-full inline-block" />
            ) : isRegister ? "Create Account" : "Login"}
          </button>

          <div className="relative py-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-800" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-slate-900 text-slate-500">or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleLogin}
            className="btn-secondary w-full flex items-center justify-center gap-2"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
        </form>

        <p className="text-center text-slate-500 mt-4">
          <Link to="/forgot-password" className="text-cyan-400 hover:underline text-sm">
            Forgot your password?
          </Link>
        </p>

        <p className="text-center text-slate-500 mt-4">
          {isRegister ? "Already have an account? " : "Don't have an account? "}
          <button
            onClick={() => setIsRegister(!isRegister)}
            className="text-cyan-400 hover:underline"
            data-testid="toggle-auth-mode"
          >
            {isRegister ? "Login" : "Register"}
          </button>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
